import { useMemo, useState } from 'react'
import Layout from '../components/Layout'
import { useAuth } from '../auth/AuthContext'
import { useTranslation } from 'react-i18next'
import { isSupabaseConfigured } from '../supabase'

type Mode = 'login' | 'signup'

function Auth() {
  const { t } = useTranslation()
  const { user, loading, signInWithPassword, signOut, signUpWithPassword } = useAuth()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const title = useMemo(() => {
    if (user) return t('auth.titleAccount')
    return mode === 'login' ? t('auth.titleLogin') : t('auth.titleSignUp')
  }, [mode, t, user])

  const handleSubmit = async () => {
    setError(null)
    setMessage(null)
    setSubmitting(true)

    try {
      if (mode === 'login') {
        const { error } = await signInWithPassword(email.trim(), password)
        if (error) setError(error)
      } else {
        const { error } = await signUpWithPassword(email.trim(), password)
        if (error) {
          setError(error)
        } else {
          setMessage(t('auth.signUpSuccess'))
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleSignOut = async () => {
    setError(null)
    setMessage(null)
    setSubmitting(true)
    try {
      const { error } = await signOut()
      if (error) setError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Layout showBackButton title={title} showBottomNav={false}>
      <div className="flex flex-col p-4 gap-6">
        {!isSupabaseConfigured && (
          <div className="card text-sm text-slate-700 dark:text-slate-200">
            {t('auth.notConfigured')}
          </div>
        )}
        {loading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">{t('auth.loading')}</div>
        ) : user ? (
          <div className="card flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div className="text-sm font-semibold text-slate-900 dark:text-white">{t('auth.signedIn')}</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">{user.email}</div>
            </div>
            <button onClick={handleSignOut} className="btn-secondary" disabled={submitting}>
              {t('auth.signOut')}
            </button>
            {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
            {message && <div className="text-sm text-slate-600 dark:text-slate-300">{message}</div>}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <button
                className={mode === 'login' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}
                onClick={() => setMode('login')}
                disabled={submitting || !isSupabaseConfigured}
              >
                {t('auth.login')}
              </button>
              <button
                className={mode === 'signup' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}
                onClick={() => setMode('signup')}
                disabled={submitting || !isSupabaseConfigured}
              >
                {t('auth.signUp')}
              </button>
            </div>

            <div className="card flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <label className="section-label">{t('auth.email')}</label>
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  autoComplete="email"
                  disabled={!isSupabaseConfigured}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="section-label">{t('auth.password')}</label>
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  disabled={!isSupabaseConfigured}
                />
              </div>

              <button
                onClick={handleSubmit}
                className="btn-primary"
                disabled={submitting || !email || !password || !isSupabaseConfigured}
              >
                {mode === 'login' ? t('auth.login') : t('auth.createAccount')}
              </button>

              {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
              {message && <div className="text-sm text-slate-600 dark:text-slate-300">{message}</div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Auth
